'use client';

import { Accordion } from '@mv/web-ui';
import type { ReactNode } from 'react';
import { useDataTags } from '@/modules/tracking/analytics/useDataTags';
import { CollapseItem } from '@/payload/blocks/Collapse/CollapseItem';
import type { RichTextOptions } from '@/payload/components/RichText/RichText';
import type { CollapseBlock as CollapseBlockProps } from '@/payload/types';
import styles from './Collapse.module.scss';

type Props = CollapseBlockProps & {
	options?: RichTextOptions;
};

export default function CollapseBlock({ blockType, id, configuration, items, options }: Props) {
	const { blockRef } = useDataTags(blockType, id);

	if (!items || items.length === 0) return null;

	options = {
		...options,
		size: 'small',
	};

	let content: ReactNode;

	const isTwoColumn = configuration?.columns === '2';
	if (isTwoColumn) {
		const half = Math.ceil(items.length / 2);
		const col1 = items.slice(0, half);
		const col2 = items.slice(half);

		content = (
			<div className={styles.twoColumns}>
				<div className={styles.column}>
					{col1.map(({ item, id }, index) => (
						<CollapseItem key={id} value={`${id ?? index}`} item={item} options={options} />
					))}
				</div>
				<div className={styles.column}>
					{col2.map(({ item, id }, index) => (
						<CollapseItem key={id} value={`${id ?? index}`} item={item} options={options} />
					))}
				</div>
			</div>
		);
	} else {
		content = items.map(({ item, id }, index) => (
			<CollapseItem key={id} value={`${id ?? index}`} item={item} options={options} />
		));
	}

	return (
		<Accordion.Root
			data-component="CollapseBlock"
			ref={blockRef}
			appearance={configuration?.appearance ?? 'open-close'}
			autoClose={!!configuration?.autoClose}
		>
			{content}
		</Accordion.Root>
	);
}
